"use client";

import { useEffect, useRef, useState } from "react";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";

type Status = "idle" | "speaking" | "paused";

function sortVoices(voices: SpeechSynthesisVoice[]): SpeechSynthesisVoice[] {
  const english = voices.filter((v) => v.lang.toLowerCase().startsWith("en"));
  const rest = voices.filter((v) => !v.lang.toLowerCase().startsWith("en"));
  const byName = (a: SpeechSynthesisVoice, b: SpeechSynthesisVoice) =>
    a.name.localeCompare(b.name);
  return [...english.sort(byName), ...rest.sort(byName)];
}

const SAMPLE_TEXT =
  "Paste your essay, article, or bibliography here and press Play to hear it read aloud.";

/** English strings. A translated content file overrides these through `ui`. */
export const TEXT_TO_SPEECH_STRINGS = {
  unsupported:
    "Your browser doesn't support speech synthesis. Try a recent version of Chrome, Edge, Safari, or Firefox.",
  textToReadAloud: "Text to read aloud",
  placeholder: SAMPLE_TEXT,
  voice: "Voice",
  englishVoicesFirst: "(English voices listed first)",
  defaultVoice: "Default voice",
  /** Appended to a voice the browser reports as remote (`localService: false`). */
  onlineVoice: "online",
  speed: "Speed:",
  pitch: "Pitch:",
  play: "Play",
  pause: "Pause",
  resume: "Resume",
  stop: "Stop",
  speaking: "Speaking…",
  paused: "Paused",
  footerNote:
    "Voices come from your browser and operating system, so the selection and sound quality vary between devices. CitationEasy never receives your text. A voice marked “online” — in Chrome, the “Google” voices — sends it to its provider's speech service; pick an unmarked, on-device voice to keep it on your device.",
} as const;

/*
 * A remote voice is not a local one with a different accent. Chrome's "Google …" voices
 * come from a built-in extension that sends the utterance text to Google's speech API
 * and plays back the audio it returns (chrome/browser/resources/network_speech_synthesis);
 * Chromium reports them with `localService: false`. Marking them is what lets the
 * privacy note above tell a reader how to keep a draft on their own device.
 */
function voiceLabel(voice: SpeechSynthesisVoice, onlineVoice: string): string {
  const label = `${voice.name} (${voice.lang})`;
  return voice.localService ? label : `${label} — ${onlineVoice}`;
}

export default function TextToSpeechWidget() {
  const strings = useToolUi(TEXT_TO_SPEECH_STRINGS);
  const fmt = useToolNumber();
  const [supported, setSupported] = useState(true);
  const [text, setText] = useState("");
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);
  const [voiceUri, setVoiceUri] = useState("");
  const [rate, setRate] = useState(1);
  const [pitch, setPitch] = useState(1);
  const [status, setStatus] = useState<Status>("idle");
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);

  useEffect(() => {
    if (typeof window === "undefined" || !("speechSynthesis" in window)) {
      setSupported(false);
      return;
    }
    const synth = window.speechSynthesis;
    const loadVoices = () => {
      // Some browsers populate the voice list asynchronously and fire
      // `voiceschanged` when it is ready, so we read it both now and then.
      setVoices(sortVoices(synth.getVoices()));
    };
    loadVoices();
    synth.addEventListener("voiceschanged", loadVoices);
    return () => {
      synth.removeEventListener("voiceschanged", loadVoices);
      synth.cancel();
    };
  }, []);

  function handlePlay() {
    if (text.trim().length === 0) {
      return;
    }
    const synth = window.speechSynthesis;
    synth.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    const voice = voices.find((v) => v.voiceURI === voiceUri);
    if (voice !== undefined) {
      utterance.voice = voice;
    }
    utterance.rate = rate;
    utterance.pitch = pitch;
    utterance.onend = () => setStatus("idle");
    utterance.onerror = () => setStatus("idle");
    utteranceRef.current = utterance;
    synth.speak(utterance);
    setStatus("speaking");
  }

  function handlePause() {
    window.speechSynthesis.pause();
    setStatus("paused");
  }

  function handleResume() {
    window.speechSynthesis.resume();
    setStatus("speaking");
  }

  function handleStop() {
    window.speechSynthesis.cancel();
    setStatus("idle");
  }

  return (
    <>
      {!supported && (
        <p className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-destructive text-sm">
          {strings.unsupported}
        </p>
      )}

      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="tts-input"
        >
          {strings.textToReadAloud}
        </label>
        <textarea
          className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="tts-input"
          onChange={(e) => setText(e.target.value)}
          placeholder={strings.placeholder}
          value={text}
        />
      </div>

      {/* Voice + sliders */}
      <section className="space-y-4">
        <div className="space-y-2">
          <label
            className="block font-medium text-foreground text-sm"
            htmlFor="tts-voice"
          >
            {strings.voice}{" "}
            <span className="font-normal text-muted-foreground">
              {strings.englishVoicesFirst}
            </span>
          </label>
          <select
            className="w-full rounded-lg border border-border bg-card p-3 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            disabled={voices.length === 0}
            id="tts-voice"
            onChange={(e) => setVoiceUri(e.target.value)}
            value={voiceUri}
          >
            <option value="">{strings.defaultVoice}</option>
            {voices.map((v) => (
              <option key={v.voiceURI} value={v.voiceURI}>
                {voiceLabel(v, strings.onlineVoice)}
              </option>
            ))}
          </select>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <label
              className="block font-medium text-foreground text-sm"
              htmlFor="tts-rate"
            >
              {strings.speed}{" "}
              {fmt(rate, {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
              })}
              ×
            </label>
            <input
              className="w-full accent-primary"
              id="tts-rate"
              max={2}
              min={0.5}
              onChange={(e) => setRate(Number.parseFloat(e.target.value))}
              step={0.1}
              type="range"
              value={rate}
            />
          </div>
          <div className="space-y-2">
            <label
              className="block font-medium text-foreground text-sm"
              htmlFor="tts-pitch"
            >
              {strings.pitch}{" "}
              {fmt(pitch, {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
              })}
            </label>
            <input
              className="w-full accent-primary"
              id="tts-pitch"
              max={2}
              min={0}
              onChange={(e) => setPitch(Number.parseFloat(e.target.value))}
              step={0.1}
              type="range"
              value={pitch}
            />
          </div>
        </div>
      </section>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={!supported || text.trim().length === 0}
          onClick={handlePlay}
          type="button"
        >
          {strings.play}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={status !== "speaking"}
          onClick={handlePause}
          type="button"
        >
          {strings.pause}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={status !== "paused"}
          onClick={handleResume}
          type="button"
        >
          {strings.resume}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={status === "idle"}
          onClick={handleStop}
          type="button"
        >
          {strings.stop}
        </button>
        {status === "speaking" && (
          <span className="flex items-center gap-2 text-primary text-sm">
            <span className="h-2 w-2 animate-pulse rounded-full bg-primary" />
            {strings.speaking}
          </span>
        )}
        {status === "paused" && (
          <span className="text-muted-foreground text-sm">
            {strings.paused}
          </span>
        )}
      </div>

      <p className="rounded-lg border border-border bg-muted/30 p-4 text-muted-foreground text-sm leading-relaxed">
        {strings.footerNote}
      </p>
    </>
  );
}
